import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import StructuredData from "../components/seo/StructuredData";
import { COURSE_CATALOG, COURSE_PROGRESS_KEY } from "../data/courseCatalog";
import { getAuthHeader } from "../utils/auth";
import { getApiBaseUrl } from "../utils/api";
import TheQAHubAcademyImage from "../images/TheQAHubAcademy.png";
import { breadcrumbSchema, courseSchema, organizationSchema, schemaGraph } from "../utils/seoData";
import "./Dashboard.css";
import "./retos/Retos.css";
import "./rutaDeEstudio/Roadmap.css";
import "./LearnCourse.css";

const API_URL = getApiBaseUrl();

export default function LearnCourse() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [courseProgress, setCourseProgress] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [apiBaseUrl, setApiBaseUrl] = useState(API_URL);

  const course = useMemo(
    () => COURSE_CATALOG.find((item) => item.id === courseId) || null,
    [courseId]
  );

  useEffect(() => {
    let active = true;

    const loadCourseProgress = async () => {
      setLoadingProgress(true);

      try {
        const storedBaseUrl = window.localStorage.getItem("theqahub_api_base") || API_URL;
        if (!storedBaseUrl) {
          if (active) setLoadingProgress(false);
          return;
        }

        if (active) setApiBaseUrl(storedBaseUrl);

        const res = await fetch(`${storedBaseUrl}/api/auth/course-progress`, {
          method: "GET",
          credentials: "include",
          headers: {
            ...getAuthHeader(),
          },
        });

        const data = await res.json().catch(() => ({}));

        if (!active) return;

        if (res.ok) {
          const serverProgress = data.courseProgress || {};
          const hasServerProgress = Object.keys(serverProgress).length > 0;

          if (!hasServerProgress) {
            try {
              const raw = window.localStorage.getItem(COURSE_PROGRESS_KEY);
              const legacyProgress = raw ? JSON.parse(raw) : {};
              const hasLegacyProgress = Object.keys(legacyProgress || {}).length > 0;

              if (hasLegacyProgress) {
                const syncRes = await fetch(`${storedBaseUrl}/api/auth/course-progress/sync`, {
                  method: "POST",
                  credentials: "include",
                  headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                  },
                  body: JSON.stringify({ courseProgress: legacyProgress }),
                });

                const syncData = await syncRes.json().catch(() => ({}));
                if (syncRes.ok) {
                  setCourseProgress(syncData.courseProgress || {});
                  window.localStorage.removeItem(COURSE_PROGRESS_KEY);
                } else {
                  setCourseProgress(legacyProgress);
                }
              } else {
                setCourseProgress({});
              }
            } catch {
              setCourseProgress(serverProgress);
            }
          } else {
            setCourseProgress(serverProgress);
            try {
              window.localStorage.removeItem(COURSE_PROGRESS_KEY);
            } catch {}
          }
        }
      } finally {
        if (active) setLoadingProgress(false);
      }
    };

    loadCourseProgress();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [courseId]);

  const selectedVideo = course?.videos[selectedIndex];
  const completedMap = course ? courseProgress[course.id] || {} : {};
  const completedCount = course
    ? course.videos.filter((video) => completedMap[video.id]).length
    : 0;
  const progress = course?.videos.length
    ? Math.round((completedCount / course.videos.length) * 100)
    : 0;

  const toggleCompleted = (videoId) => {
    if (!course) return;

    const nextCompleted = !completedMap[videoId];
    const previousProgress = courseProgress;

    setCourseProgress((prev) => {
      const nextProgress = { ...prev };
      const currentCourseProgress = { ...(nextProgress[course.id] || {}) };

      if (nextCompleted) {
        currentCourseProgress[videoId] = true;
        nextProgress[course.id] = currentCourseProgress;
      } else {
        delete currentCourseProgress[videoId];
        if (Object.keys(currentCourseProgress).length > 0) {
          nextProgress[course.id] = currentCourseProgress;
        } else {
          delete nextProgress[course.id];
        }
      }

      return nextProgress;
    });

    fetch(`${apiBaseUrl}/api/auth/course-progress`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        courseId: course.id,
        videoId,
        completed: nextCompleted,
      }),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(async ({ ok, data }) => {
        if (ok) {
          const nextProgress = data.courseProgress || {};
          setCourseProgress(nextProgress);

          const completedVideosAfterUpdate = course.videos.filter(
            (video) => nextProgress?.[course.id]?.[video.id]
          ).length;

          if (completedVideosAfterUpdate === course.videos.length) {
            await fetch(`${apiBaseUrl}/api/auth/course-progress/complete`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...getAuthHeader(),
              },
              body: JSON.stringify({
                courseId: course.id,
                courseTitle: course.title,
              }),
            }).catch(() => {});
          }
          return;
        }
        setCourseProgress(previousProgress);
      })
      .catch(() => {
        setCourseProgress(previousProgress);
      });
  };

  if (!course) {
    return (
      <div className="dash-page retos-page">
        <div className="roadmap-page learn-course-page">
          <div className="dash-hero">
            <button className="dash-btn ghost roadmap" onClick={() => navigate("/dashboard")}>
              ← Volver al dashboard
            </button>
          </div>

          <div className="roadmap-card">
            <div className="reto-card wide">
              <div className="reto-card-head">
                <h2>Curso no encontrado</h2>
              </div>
              <p className="muted">
                La serie que intentas abrir no existe o todavía no está disponible.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadingProgress) {
    return (
      <div className="dash-page retos-page">
        <div className="roadmap-page learn-course-page">
          <div className="dash-skeleton">Cargando progreso del curso...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-page retos-page" translate="no">
      <StructuredData
        data={schemaGraph([
          organizationSchema(TheQAHubAcademyImage),
          courseSchema(course),
          breadcrumbSchema([
            { name: "Inicio", url: "/" },
            { name: "Aprende", url: "/aprende" },
            { name: course.title, url: `/learn/${course.id}` },
          ]),
        ])}
      />
      <div className="roadmap-page learn-course-page">
        <div className="dash-hero">
          <button
            className="dash-btn ghost roadmap"
            onClick={() => navigate("/dashboard")}
            style={{ marginBottom: 12 }}
          >
            ← Volver al dashboard
          </button>

          <div>
            <h1>{course.title}</h1>
            <p className="muted learn-course-copy">
              Recorre todos los vídeos de esta serie y marca cada lección como completada
              cuando termines para actualizar tu progreso en el dashboard.
            </p>

            <div className="dash-quick">
              <div className="quick-card">
                <div className="quick-title">Progreso del curso</div>
                <div className="quick-value">{progress}%</div>
                <div className="progress">
                  <div className="progress-bar" style={{ width: `${progress}%` }} />
                </div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Vídeos completados</div>
                <div className="quick-value small">
                  {completedCount}/{course.videos.length}
                </div>
                <div className="muted small">Cada vídeo completado suma a “Mis cursos”</div>
              </div>

              <div className="quick-card">
                <div className="quick-title">Nivel</div>
                <div className="quick-value small">{course.level}</div>
                <div className="muted small">Serie guiada por episodios</div>
              </div>
            </div>
          </div>
        </div>

        <div className="roadmap-card learn-course-shell">
          <section className="reto-card wide">
            <div className="reto-card-head">
              <h2>Lección actual</h2>
              <span className="reto-badge">Vídeo {selectedIndex + 1}/{course.videos.length}</span>
            </div>

            <div className="learn-course-player">
              <div className="learn-course-video-frame">
                <iframe
                  title={selectedVideo.title}
                  src={selectedVideo.embed}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              <div className="learn-course-sidebar">
                <div className="quest-title">{selectedVideo.title}</div>
                <div className="meta-row">
                  <span className={`pill ${completedMap[selectedVideo.id] ? "pill-green" : "pill-gray"}`}>
                    {completedMap[selectedVideo.id] ? "Completado" : "Pendiente"}
                  </span>
                  <span className="pill pill-gray">Serie {course.title}</span>
                </div>

                <p className="muted learn-course-progress-text">
                  Este porcentaje se refleja también en “Mis cursos”.
                </p>

                <div className="learn-course-actions">
                  <a
                    className="dash-btn ghost"
                    href={selectedVideo.watchUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ver en YouTube
                  </a>

                  <button
                    className="dash-btn"
                    onClick={() => toggleCompleted(selectedVideo.id)}
                  >
                    {completedMap[selectedVideo.id] ? "Quitar completado" : "Marcar completado"}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="reto-card wide">
            <div className="reto-card-head">
              <h2>Vídeos de la serie</h2>
              <span className="muted small">Muévete entre episodios y entra donde lo dejaste</span>
            </div>

            <Swiper
              modules={[Navigation]}
              navigation
              spaceBetween={12}
              slidesPerView={1.1}
              breakpoints={{
                700: { slidesPerView: 2.1 },
                980: { slidesPerView: 3 },
              }}
            >
              {course.videos.map((video, index) => (
                <SwiperSlide key={video.id}>
                  <button
                    type="button"
                    className={`learn-course-slide ${selectedIndex === index ? "active" : ""}`}
                    onClick={() => setSelectedIndex(index)}
                  >
                    <div className="learn-course-slide-top">
                      <span className={`pill ${completedMap[video.id] ? "pill-green" : "pill-gray"}`}>
                        {completedMap[video.id] ? "Completado" : "Pendiente"}
                      </span>
                      <span className="learn-course-slide-count">
                        {index + 1}/{course.videos.length}
                      </span>
                    </div>
                    <div className="learn-course-slide-title">{video.title}</div>
                    <div className="muted small learn-course-slide-copy">
                      {completedMap[video.id]
                        ? "Esta lección ya suma al progreso del curso."
                        : "Selecciona este vídeo para verlo o marcarlo cuando lo termines."}
                    </div>
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
          </section>

          <section className="reto-card wide">
            <div className="reto-card-head">
              <h2>Listado completo</h2>
              <span className="muted small">Acceso rápido a toda la serie</span>
            </div>

            <div className="learn-course-list">
              {course.videos.map((video, index) => (
                <div key={video.id} className="learn-course-list-item">
                  <button
                    type="button"
                    className="learn-course-list-main"
                    onClick={() => setSelectedIndex(index)}
                  >
                    <span className="learn-course-list-number">{index + 1}.</span>
                    <span>{video.title}</span>
                  </button>

                  <button
                    type="button"
                    className={`learn-course-list-complete ${completedMap[video.id] ? "done" : ""}`}
                    onClick={() => toggleCompleted(video.id)}
                  >
                    {completedMap[video.id] ? "Completado" : "Completar"}
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
